"use client";

import { Container, clx } from "@medusajs/ui"
import Image from "next/image"
import React from "react"

import PlaceholderImage from "@modules/common/icons/placeholder-image"
import LayeredThumbnail from "../layered-thumbnail"

type ThumbnailProps = {
  thumbnail?: string | null
  // TODO: Fix image typings
  images?: any[] | null
  size?: "small" | "medium" | "large" | "full" | "square"
  isFeatured?: boolean
  className?: string
  "data-testid"?: string
  // For cart items with options
  metadata?: Record<string, unknown>
}

const Thumbnail: React.FC<ThumbnailProps> = ({
  thumbnail,
  images,
  size = "small",
  isFeatured,
  className,
  "data-testid": dataTestid,
  metadata,
}) => {
  // Determine if we should use layered images
  const shouldUseLayeredImages = images && images.length > 0 && (
    // Either we have metadata (cart context)
    metadata ||
    // Or we have images that follow the naming convention for layered images
    images.some(img => {
      const url = decodeURIComponent(img.url);
      return url.includes("Base_") || url.includes("Color_");
    })
  );

  const initialImage = images?.[0]?.url;

  return (
    <Container
      className={clx(
        "relative w-full overflow-hidden bg-[#00000057] text-white backdrop-blur-lg shadow-xl rounded-sm shadow-elevation-card-rest group-hover:shadow-elevation-card-hover transition-shadow ease-in-out duration-150",
        size === "square" ? "p-2" : "p-4",
        className,
        {
          "aspect-[11/14]": isFeatured,
          "aspect-[9/16]": !isFeatured && size !== "square",
          "aspect-[1/1]": size === "square",
          "w-[180px]": size === "small",
          "w-[290px]": size === "medium",
          "w-[440px]": size === "large",
          "w-full": size === "full",
          "max-h-[500px]": true,
        }
      )}
      data-testid={dataTestid}
    >
      {shouldUseLayeredImages ? (
        <LayeredThumbnail
          images={images}
          size={size}
          metadata={metadata}
          options={metadata || {}}
        />
      ) : (
        <ImageOrPlaceholder image={thumbnail || initialImage} size={size} />
      )}
    </Container>
  )
}

const ImageOrPlaceholder = ({
  image,
  size,
}: Pick<ThumbnailProps, "size"> & { image?: string }) => {
  return image ? (
    <Image
      src={image}
      alt="Thumbnail"
      className="absolute inset-0 object-contain object-center max-h-[400px] mx-auto"
      draggable={false}
      quality={50}
      sizes="(max-width: 576px) 280px, (max-width: 768px) 360px, (max-width: 992px) 480px, 800px"
      fill
      unoptimized
    />
  ) : (
    <div className="w-full h-full absolute inset-0 flex items-center justify-center">
      <PlaceholderImage size={size === "small" ? 16 : 24} />
    </div>
  )
}

export default Thumbnail
